// ---------------------------------------------------------------------------
// Motor de créditos — fonte única de verdade.
//
// Este é o primeiro módulo no formato da futura API: toda função recebe o
// cliente do banco como primeiro parâmetro (o navegador passa o cliente do
// usuário; o servidor passará o de serviço) e nenhuma sabe que React existe.
//
// Semântica unificada — a MESMA para professor e aluno:
//   total    = total contratado (contratos.total_aulas)
//   usado    = realizadas + faltas DESTE contrato + aulas_realizadas_anterior
//   restante = max(0, total - usado)
// Cancelamentos (pelo aluno ou pelo professor) NÃO consomem crédito.
// ---------------------------------------------------------------------------

export const STATUS_QUE_CONSOMEM = ['realizada', 'falta'];

// Fórmula pura — usada pelas duas interfaces para exibir, sem tocar no banco.
export function calcularCreditos(contrato, aulasDoContrato) {
  const anteriores = Number(contrato?.aulas_realizadas_anterior) || 0;
  const consumidas = (aulasDoContrato || [])
    .filter(a => STATUS_QUE_CONSOMEM.includes(a.status)).length;
  const total = Number(contrato?.total_aulas) || 0;
  const usado = consumidas + anteriores;
  // Passar do total e' possivel de verdade (aula extra combinada), mas tambem e' o
  // sintoma de dado sujo. Separar o excedente deixa a barra honesta: ela nunca mostra
  // "13 de 8" como se fosse normal — mostra 8 de 8 e avisa que ha' 5 aulas a mais.
  const excedente = Math.max(0, usado - total);
  return {
    total,
    usado: Math.min(usado, total || usado),
    usado_bruto: usado,
    excedente,
    restante: Math.max(0, total - usado),
  };
}

// Recalcula a partir do que existe de fato e persiste na tabela creditos.
// Chamar após mutação de aula ou contrato feita pelo app. Idempotente.
// v131: as funções novas do banco (aluno_avisar_falta, desfazer_desfecho_aula,
// professor_cancelar_aula, professor_decidir_aviso...) já recalculam no servidor e devolvem
// `saldo` — depois delas o app NÃO chama esta função (saldo null = acordo sem saldo de aulas:
// não mexer no anel).
export async function recalcularCreditos(db, alunoId) {
  const { data: contratos, error: eCt } = await db.from('contratos')
    .select('id, professor_id, total_aulas, aulas_realizadas_anterior, tipo_cobranca, data_inicio, turma_id')
    .eq('aluno_id', alunoId).eq('status', 'ativo')
    .order('data_inicio', { ascending: false });
  if (eCt) { console.warn('recalcularCreditos/contrato:', eCt.message); return { error: eCt }; }
  const ativos = contratos || [];
  if (!ativos.length) return { semContrato: true };
  // v131: quem tem PACOTE ativo e também uma mensalidade (ex.: pacote + turma) fica com o saldo
  // do pacote. Antes valia "o acordo mais recente": se fosse a mensalidade da turma, o saldo do
  // pacote era APAGADO. Entre pacotes, o sem turma primeiro (a mesma escolha do banco).
  const pacotes = ativos.filter(c => c.tipo_cobranca !== 'mensal');
  const contrato = pacotes.find(c => !c.turma_id) || pacotes[0] || ativos[0];

  // Mensalidade nao tem saldo de aulas: o aluno paga o mes, treinando ou nao. Criar um
  // registro de credito aqui faz a tela dele mostrar um anel "0 de 0 aulas" — um numero
  // que nao quer dizer nada. Apago o que ja' existir e saio (so' quando NAO ha' pacote ativo).
  if (contrato.tipo_cobranca === 'mensal') {
    await db.from('creditos').delete().eq('aluno_id', alunoId);
    return { mensal: true };
  }

  const { data: aulas, error: eAu } = await db.from('aulas')
    .select('status').eq('aluno_id', alunoId).eq('contrato_id', contrato.id);
  if (eAu) { console.warn('recalcularCreditos/aulas:', eAu.message); return { error: eAu }; }

  const { total, usado } = calcularCreditos(contrato, aulas);

  const { data: existente, error: eEx } = await db.from('creditos')
    .select('id').eq('aluno_id', alunoId).limit(1);
  if (eEx) { console.warn('recalcularCreditos/leitura:', eEx.message); return { error: eEx }; }

  let error;
  if (existente && existente.length > 0) {
    ({ error } = await db.from('creditos')
      .update({ contrato_id: contrato.id, total, usado }).eq('id', existente[0].id));
  } else {
    ({ error } = await db.from('creditos')
      .insert({ aluno_id: alunoId, contrato_id: contrato.id, professor_id: contrato.professor_id, total, usado }));
  }
  if (error) console.warn('recalcularCreditos/escrita:', error.message);
  return { error, total, usado };
}
